import { useEffect } from 'react';
import { fullTitle as buildTitle } from '../utils/pageTitle';

const SITE_NAME = 'Buy Sell Taxi';
const BASE_URL = 'https://www.buyselltaxi.com';
const DEFAULT_DESCRIPTION =
  "India's trusted marketplace for buying and selling taxis, commercial cars, mini vans and buses. Verified listings, BST Yard support & transparent pricing.";
const DEFAULT_IMAGE = `${BASE_URL}/og-image.jpg`;
const DEFAULT_KEYWORDS =
  'buy taxi in Delhi, used taxi Delhi, second hand taxi Delhi, buy sell taxi India, commercial vehicle marketplace India, taxi for sale India, buy taxi online India, used taxi Mumbai, used taxi Bangalore, sell taxi India, CNG taxi for sale Delhi, taxi marketplace India';

/**
 * useSEO – dynamically updates <title>, meta tags, OG, Twitter Card, canonical & JSON-LD
 *
 * @param {Object} options
 * @param {string}  [options.title]        – Page title (without site name suffix)
 * @param {string}  [options.description]  – Meta description (max ~155 chars)
 * @param {string}  [options.keywords]     – Comma-separated keywords
 * @param {string}  [options.image]        – Absolute URL for OG / Twitter image
 * @param {string}  [options.url]          – Canonical URL for this page
 * @param {string}  [options.type]         – OG type: 'website' | 'article' | 'product' (default: 'website')
 * @param {boolean} [options.noindex]      – Set true for admin/seller private pages
 * @param {boolean} [options.follow]        – With noindex, keep 'follow' so internal
 *                                            links still pass equity. Default false
 *                                            (noindex, nofollow) for private pages.
 * @param {Object}  [options.jsonLd]       – Optional JSON-LD object to inject as <script>
 */
const useSEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords = DEFAULT_KEYWORDS,
  image = DEFAULT_IMAGE,
  url,
  type = 'website',
  noindex = false,
  follow = false,
  jsonLd = null,
} = {}) => {
  useEffect(() => {
    // ── Full page title ──────────────────────────────────────────────────────
    // Brand suffix only when it fits — see src/utils/pageTitle.js.
    const fullTitle = buildTitle(title);
    document.title = fullTitle;

    // ── Helpers ──────────────────────────────────────────────────────────────
    const setMeta = (name, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setProperty = (property, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[property="${property}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setLink = (rel, href, extra = {}) => {
      if (!href) return;
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
      Object.entries(extra).forEach(([k, v]) => el.setAttribute(k, v));
    };

    // Strip trailing slash so /vehicles/ and /vehicles are treated as the same canonical.
    // Exception: the bare domain (https://www.buyselltaxi.com) keeps no slash.
    const rawUrl = url || BASE_URL;
    const canonicalUrl = rawUrl.endsWith('/') && rawUrl !== BASE_URL + '/'
      ? rawUrl.slice(0, -1)
      : rawUrl.replace(/\/$/, '');

    // ── Primary SEO ──────────────────────────────────────────────────────────
    setMeta('description', description);
    setMeta('keywords', keywords);
    // A public page can be deliberately de-indexed while still passing link equity
    // (the zero-stock city pages). Private admin/seller screens keep nofollow.
    const robots = noindex
      ? (follow ? 'noindex, follow' : 'noindex, nofollow')
      : 'index, follow, max-snippet:-1, max-image-preview:large';
    setMeta('robots', robots);
    setMeta('author', SITE_NAME);

    // ── Open Graph ───────────────────────────────────────────────────────────
    setProperty('og:title', fullTitle);
    setProperty('og:description', description);
    setProperty('og:image', image);
    setProperty('og:image:alt', `${fullTitle} – Buy Sell Taxi`);
    setProperty('og:url', canonicalUrl);
    setProperty('og:type', type);
    setProperty('og:site_name', SITE_NAME);
    setProperty('og:locale', 'en_IN');

    // Remove fixed og:image dimensions when using a non-default image (e.g. Cloudinary)
    // so crawlers don't assume the static 1200×630 from index.html applies.
    if (image !== DEFAULT_IMAGE) {
      document.querySelector('meta[property="og:image:width"]')?.remove();
      document.querySelector('meta[property="og:image:height"]')?.remove();
    }

    // ── Twitter Card ─────────────────────────────────────────────────────────
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', description);
    setMeta('twitter:image', image);
    setMeta('twitter:image:alt', fullTitle);

    // ── Canonical Link ───────────────────────────────────────────────────────
    setLink('canonical', canonicalUrl);

    // ── JSON-LD Structured Data ──────────────────────────────────────────────
    document.querySelectorAll('[id^="page-jsonld"]').forEach(el => el.remove());

    if (jsonLd) {
      const schemas = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      schemas.forEach((schema, i) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.id = `page-jsonld-${i}`;
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);
      });
    }

    // ── Cleanup on unmount ───────────────────────────────────────────────────
    return () => {
      document.querySelectorAll('[id^="page-jsonld"]').forEach(el => el.remove());
    };
  }, [title, description, keywords, image, url, type, noindex, follow, jsonLd]);
};

export default useSEO;
